"use client";

import Link from "next/link";
import { useId, useState, useTransition } from "react";
import { addLineItem, openCartDrawer } from "../../lib/cart-store";
import type { CatalogVariant } from "../../lib/types";

type Option = { name: string; choices: string[] };

/**
 * PDP add-to-cart. Renders an option selector (Size, etc.) for variant-managed
 * products and resolves the chosen combination to the Wix variantId that the
 * V1 cart API requires on catalogReference.options.
 */
export default function AddToCart({
  productId,
  inStock,
  manageVariants,
  options,
  variants,
  askUrl,
}: {
  productId: string;
  inStock: boolean;
  manageVariants: boolean;
  options: Option[];
  variants: CatalogVariant[];
  /** WhatsApp "ask about it" link shown in place of a dead end when sold out. */
  askUrl?: string;
}) {
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [pending, startTransition] = useTransition();
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const uid = useId();

  function resolveVariantId(): string | undefined {
    if (!manageVariants) return undefined;
    const match = variants.find((v) =>
      Object.entries(selected).every(
        ([name, val]) => String(v.choices[name] ?? "").toLowerCase() === val.toLowerCase(),
      ),
    );
    return match?.variantId;
  }

  /**
   * Whether the shopper must choose something before this product can be added.
   *
   * This was `manageVariants && options.length > 0`, which is false for every
   * product in this store: all 110 have manageVariants false, while 16 of them
   * carry a real Size option (S/M/L). The picker below therefore never rendered
   * and those 16 went into the cart — and on to Wix checkout — with no size,
   * leaving the merchant a paid order and no idea which size to ship.
   *
   * The presence of options is what decides it. How the choice is then sent to
   * Wix depends on manageVariants, and that branch lives in handleAdd.
   */
  const needsSelection = options.length > 0;
  const allChosen = !needsSelection || options.every((o) => selected[o.name]);
  const variantId = resolveVariantId();
  const variantInStock =
    !needsSelection || !variantId
      ? inStock
      : variants.find((v) => v.variantId === variantId)?.inStock ?? inStock;

  function handleAdd() {
    if (pending) return; // aria-disabled, not disabled, so focus stays put
    setError(null);
    setDone(false);
    if (needsSelection && !allChosen) {
      setError("Please select an option.");
      return;
    }
    // Variant-managed products resolve to a variantId; option-only products
    // send the raw choices, which is the shape Wix V1 wants for them.
    if (needsSelection && manageVariants && !variantId) {
      setError("That combination isn't available.");
      return;
    }
    startTransition(async () => {
      try {
        await addLineItem({
          productId,
          variantId,
          choices: manageVariants ? undefined : selected,
          quantity: 1,
        });
        setDone(true);
        // Same as the grid's quick-add: show the bag the moment it changes,
        // so the shopper sees what went in and can head to checkout from there.
        openCartDrawer();
      } catch (err) {
        console.error("[add-to-cart] failed", err);
        setError("Could not add to cart — please try again.");
      }
    });
  }

  return (
    <div className="space-y-6">
      {needsSelection &&
        options.map((opt) => {
          const labelId = `${uid}-${opt.name}`;
          return (
            <div key={opt.name}>
              <p id={labelId} className="eyebrow mb-2 text-ink/60">
                {opt.name}
              </p>
              <div role="group" aria-labelledby={labelId} className="flex flex-wrap gap-2">
                {opt.choices.map((c) => {
                  // A choice is sold out when every variant carrying it is out
                  // of stock (only knowable for variant-managed products).
                  const carrying = manageVariants
                    ? variants.filter((v) => String(v.choices[opt.name] ?? "").toLowerCase() === c.toLowerCase())
                    : [];
                  const soldOut = carrying.length > 0 && carrying.every((v) => !v.inStock);
                  const active = selected[opt.name] === c;
                  return (
                    <button
                      key={c}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setSelected((s) => ({ ...s, [opt.name]: c }))}
                      className={`border px-4 py-2 text-[12px] uppercase tracking-wide transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink ${
                        active ? "border-ink bg-ink text-paper" : "border-ink/30 hover:border-ink"
                      } ${soldOut ? "line-through decoration-1" : ""}`}
                    >
                      {c}
                      {soldOut && <span className="sr-only"> (sold out)</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

      <button
        type="button"
        onClick={handleAdd}
        disabled={!allChosen || !variantInStock}
        aria-disabled={pending || undefined}
        className="w-full bg-ink px-8 py-4 text-[12px] uppercase tracking-[0.18em] text-paper transition hover:bg-gold-deep disabled:cursor-not-allowed disabled:opacity-40 aria-disabled:cursor-not-allowed aria-disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
      >
        {!variantInStock ? "Sold Out" : pending ? "Adding…" : done ? "Added ✓" : "Add to Cart"}
      </button>

      {!variantInStock && askUrl && (
        <p className="text-center text-[12px] uppercase tracking-[0.12em] text-ink/60">
          Sold out online.{" "}
          <a
            href={askUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-ink underline underline-offset-4 transition hover:text-gold-deep"
          >
            Ask the store on WhatsApp
          </a>
        </p>
      )}

      {/* Always mounted so the change is announced rather than silently inserted. */}
      <p role="status" className="sr-only">
        {done ? "Added to cart" : ""}
      </p>

      {done && (
        <Link href="/cart" className="block text-center text-[12px] uppercase tracking-wide underline">
          View Cart →
        </Link>
      )}
      {error && (
        <p role="alert" className="text-center text-[12px] text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
